import React, { useState } from 'react'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import {
  AppBar,
  Box,
  CssBaseline,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  Menu,
  MenuItem,
  Avatar,
  Grid
} from '@mui/material'
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  AttachMoney as MoneyIcon,
  ContactMail as ContactIcon,
  Help as HelpIcon,
  Article as ArticleIcon
} from '@mui/icons-material'

const MarketingLayout: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogin = () => {
    navigate('/auth/login')
    handleMenuClose()
  }

  const handleRegister = () => {
    navigate('/register')
    handleMenuClose()
  }

  const menuItems = [
    { text: 'Home', path: '/', icon: <HomeIcon /> },
    { text: 'Features', path: '/features', icon: <InfoIcon /> },
    { text: 'Pricing', path: '/pricing', icon: <MoneyIcon /> },
    { text: 'About', path: '/about', icon: <InfoIcon /> },
    { text: 'Blog', path: '/blog', icon: <ArticleIcon /> },
    { text: 'Contact', path: '/contact', icon: <ContactIcon /> },
    { text: 'FAQ', path: '/faq', icon: <HelpIcon /> }
  ]

  const drawer = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ color: '#8B4513', fontWeight: 600, flexGrow: 1 }}>
          CigarTrack
        </Typography>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path)
                handleDrawerToggle()
              }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'rgba(139, 69, 19, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(139, 69, 19, 0.2)'
                  }
                }
              }}
            >
              <ListItemText 
                primary={item.text}
                sx={{ 
                  color: location.pathname === item.path ? '#8B4513' : 'inherit',
                  '& .MuiListItemText-primary': {
                    fontWeight: location.pathname === item.path ? 600 : 400
                  }
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogin}>
            <ListItemText primary="Login" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={handleRegister}>
            <ListItemText primary="Sign Up" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'white',
          color: '#8B4513',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: theme.zIndex.drawer + 1
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              cursor: 'pointer',
              color: '#8B4513'
            }}
            onClick={() => navigate('/')}
          >
            CigarTrack
          </Typography>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            {menuItems.map((item) => (
              <Button
                key={item.text}
                color="inherit"
                onClick={() => navigate(item.path)}
                sx={{
                  color: location.pathname === item.path ? '#8B4513' : '#666',
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(139, 69, 19, 0.1)'
                  }
                }}
              >
                {item.text}
              </Button>
            ))}
          </Box>

          {/* Auth Buttons */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, ml: 2 }}>
            <Button
              variant="outlined"
              startIcon={<LoginIcon />}
              onClick={handleLogin}
              sx={{
                borderColor: '#8B4513',
                color: '#8B4513',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#7A3D0F',
                  backgroundColor: 'rgba(139, 69, 19, 0.1)'
                }
              }}
            >
              Login
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleRegister}
              sx={{
                backgroundColor: '#8B4513',
                color: 'white',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#7A3D0F'
                }
              }}
            >
              Sign Up
            </Button>
          </Box>

          {/* Mobile Auth Menu */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#8B4513' }}>
                <LoginIcon />
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleLogin}>
                <LoginIcon sx={{ mr: 1 }} />
                Login
              </MenuItem>
              <MenuItem onClick={handleRegister}>
                <PersonAddIcon sx={{ mr: 1 }} />
                Sign Up
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: 8, // Account for AppBar height
          backgroundColor: '#ffffff'
        }}
      >
        <Outlet />
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          backgroundColor: '#8B4513',
          color: 'white',
          py: 4,
          mt: 'auto'
        }}
      >
        <Box sx={{ maxWidth: 'lg', mx: 'auto', px: 2 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                CigarTrack
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                The ultimate platform for cigar collection management and enjoyment.
              </Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Product
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button color="inherit" onClick={() => navigate('/features')} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                  Features
                </Button>
                <Button color="inherit" onClick={() => navigate('/pricing')} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                  Pricing
                </Button>
                <Button color="inherit" onClick={() => navigate('/about')} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                  About
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Support
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button color="inherit" onClick={() => navigate('/contact')} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                  Contact
                </Button>
                <Button color="inherit" onClick={() => navigate('/faq')} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                  FAQ
                </Button>
                <Button color="inherit" onClick={() => navigate('/blog')} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                  Blog
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Legal
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button color="inherit" onClick={() => navigate('/privacy')} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                  Privacy Policy
                </Button>
                <Button color="inherit" onClick={() => navigate('/terms')} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                  Terms of Service
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Connect
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  color="inherit"
                  component="a"
                  href="https://twitter.com/cigartrack"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  Twitter
                </Button>
                <Button
                  color="inherit"
                  component="a"
                  href="https://facebook.com/cigartrack"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  Facebook
                </Button>
                <Button
                  color="inherit"
                  component="a"
                  href="https://instagram.com/cigartrack"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  Instagram
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', mt: 3, pt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              © {new Date().getFullYear()} CigarTrack. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default MarketingLayout

